// Formulário do perfil de jogador: rosto (foto ou avatar), apelido e, opcional, a cidade da lista do IBGE com a
// chave "Aparecer no ranking da cidade", que nasce ligada. Usado no convite (sem cidade), no primeiro acesso (com o
// celular da conta Google logo depois do apelido) e na tela de perfil.
import { useState } from 'react';
import { AVATARES, imagemDoAvatar } from '@truco6/ui';
import { ofensivo } from '@truco6/moderacao';
import { comCidade, comRanking, conta, type PerfilConta, type Sessao } from '../comum/conta.ts';
import { ChaveRankingCidade } from '../comum/RankingDaCidade.tsx';
import { iniciais } from '../comum/Rosto.tsx';
import { CampoCidade } from './CampoCidade.tsx';

export interface OpcoesForm {
  inicial: PerfilConta;
  sessao: Sessao | null; // sem sessão (convite), a foto fica para depois
  local: boolean; // mostra a cidade e a chave do ranking da cidade
  botao: string;
  ocupado?: boolean;
  depoisDoApelido?: React.ReactNode; // o celular no primeiro acesso com Google
  podeSalvar?: boolean; // false: o botão fica desabilitado (celular ainda fora do formato)
  aoSalvar(p: PerfilConta): void | Promise<void>;
}

// Recorta no centro, reduz para 256 px e converte. Safari não gera WebP: vai JPEG.
async function prepararFoto(arquivo: File): Promise<Blob> {
  const img = await createImageBitmap(arquivo);
  const lado = Math.min(img.width, img.height), tela = document.createElement('canvas');
  tela.width = tela.height = 256;
  tela.getContext('2d')!.drawImage(img, (img.width - lado) / 2, (img.height - lado) / 2, lado, lado, 0, 0, 256, 256);
  const gerar = (tipo: string) => new Promise<Blob | null>((ok) => tela.toBlob(ok, tipo, 0.85));
  const webp = await gerar('image/webp');
  if (webp?.type === 'image/webp') return webp;
  const jpeg = await gerar('image/jpeg');
  if (!jpeg) throw new Error('Não deu para ler essa foto.');
  return jpeg;
}

export function FormPerfil({ inicial, sessao, local, botao, ocupado = false, depoisDoApelido, podeSalvar = true, aoSalvar }: OpcoesForm) {
  const [p, setP] = useState<PerfilConta>(inicial);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [cidadePendente, setCidadePendente] = useState(false);
  const [erroCidade, setErroCidade] = useState<string | null>(null);
  const podeFoto = !!sessao && !sessao.anonimo && conta.urlFoto(sessao.id, 'foto-x') !== null;
  const foto = sessao ? conta.urlFoto(sessao.id, p.avatar) : null;
  const src = foto ?? imagemDoAvatar(p.avatar);
  const apelido = p.apelido.trim();

  async function trocarFoto(arquivo: File | undefined) {
    if (!arquivo || !sessao) return;
    setErro(null); setEnviando(true);
    try { setP({ ...p, avatar: await conta.enviarFoto(sessao, await prepararFoto(arquivo)) }); }
    catch (e) { setErro((e as Error).message || 'Não deu para enviar a foto.'); }
    finally { setEnviando(false); }
  }

  return (
    <form className="form" onSubmit={(ev) => {
      ev.preventDefault();
      if (!apelido) { setErro('Escolha um apelido.'); return; }
      // O mesmo filtro de palavras ofensivas do nome de sala.
      if (ofensivo(apelido)) { setErro('Escolha outro apelido.'); return; }
      // Só cidade da lista: texto que não virou uma opção não salva.
      if (local && cidadePendente) { setErroCidade('Escolha a cidade na lista.'); return; }
      setErro(null);
      void Promise.resolve(aoSalvar({ ...p, apelido }))
        .catch((e: Error) => setErro(e.message || 'Não deu para salvar. Tente de novo.'));
    }}>
      <div className="rosto-grande">
        <div className="av" style={{ width: 112, height: 112, borderColor: 'var(--nos-400)', fontSize: 40 }}>
          {src ? <img src={src} alt="Seu rosto" /> : <span>{iniciais(apelido || '?')}</span>}
        </div>
        {podeFoto && (
          <label className="link">
            {enviando ? 'Enviando...' : 'Trocar foto'}
            <input type="file" accept="image/*" hidden disabled={enviando} onChange={(ev) => void trocarFoto(ev.target.files?.[0])} />
          </label>
        )}
      </div>
      <fieldset className="avatares">
        <legend>{podeFoto ? 'Ou escolha um avatar' : 'Escolha seu avatar'}</legend>
        {AVATARES.map((a) => (
          <button key={a.id} type="button" className={'avatar' + (p.avatar === a.id ? ' on' : '')} aria-pressed={p.avatar === a.id}
            aria-label={'Avatar ' + a.nome} onClick={() => setP({ ...p, avatar: a.id })}>
            <img src={imagemDoAvatar(a.id)!} alt="" />
          </button>
        ))}
      </fieldset>
      <label className="campo">
        <span>Apelido</span>
        <input value={p.apelido} maxLength={20} autoComplete="nickname" placeholder="Como a mesa te chama"
          onChange={(ev) => setP({ ...p, apelido: ev.target.value })} />
        <small>É assim que a mesa vai te chamar.</small>
      </label>
      {depoisDoApelido}
      {local && (
        <>
          <CampoCidade codigo={p.cidadeIbge} erro={erroCidade} aoMudar={(cidadeIbge, pendente) => {
            setCidadePendente(pendente); setErroCidade(null);
            setP((a) => comCidade(a, cidadeIbge));
          }} />
          <ChaveRankingCidade codigo={p.cidadeIbge} ligado={!!p.rankingRegionalEm} aoMudar={(ligado) => setP((a) => comRanking(a, ligado))} />
        </>
      )}
      {erro && <p className="erro" role="alert">{erro}</p>}
      <button className="btn1 largo" disabled={ocupado || enviando || !podeSalvar}>{ocupado ? 'UM INSTANTE...' : botao}</button>
    </form>
  );
}
